import { useTranslation } from 'react-i18next';
import { Avatar, Badge, Card, Divider, Group, Skeleton, Stack, Text } from '@mantine/core';
import { useProfile } from '@/hooks/useProfile';
import { useCompanyCard } from '@/hooks/useCompanyCard';
import type { UserProfile } from '@/types/auth';
import InfoRow from '@/components/common/InfoRow';

function initials(profile: UserProfile): string {
  if (profile.firstName || profile.lastName) {
    return `${profile.firstName?.[0] ?? ''}${profile.lastName?.[0] ?? ''}`.toUpperCase();
  }
  if (profile.name) {
    return profile.name.slice(0, 2).toUpperCase();
  }
  return (profile.email?.[0] ?? '?').toUpperCase();
}

// Вкладка «Профиль» — только для чтения: личные данные из реестра + руководитель организации (ФИО/
// должность из ЕГРЮЛ/ДаData). Контактное лицо и телефоны организации здесь НЕ ведутся — единый источник
// «Моя компания → Контактные лица» (contact_phones, основной = is_main → идёт в документы).
// См. docs/plans/ContactPersonUnification.md (вариант A).
export default function ProfileTab() {
  const { t } = useTranslation();
  const { data: profile, isLoading, isError } = useProfile();
  const { data: company } = useCompanyCard();

  if (isError) {
    return (
      <Card withBorder padding="lg">
        <Text c="red">{t('auth.errors.generic')}</Text>
      </Card>
    );
  }

  if (isLoading || !profile) {
    return (
      <Stack gap="sm">
        <Skeleton h={64} />
        <Skeleton h={120} />
      </Stack>
    );
  }

  const fullName = [profile.lastName, profile.firstName, profile.patronymicName]
    .filter(Boolean)
    .join(' ');
  // Тип — из строкового profile.type; если бэкенд не прислал — эвристика по наличию названия организации.
  const typeLabel =
    profile.type === 'LEGAL_ENTITY'
      ? t('app.profile.typeLegal')
      : profile.type === 'SELF_EMPLOYED'
        ? t('app.profile.typeSelf')
        : profile.type === 'INDIVIDUAL'
          ? t('app.profile.typeIndividual')
          : profile.name
            ? t('app.profile.typeLegal')
            : t('app.profile.typeSelf');

  const managementName = company?.managementName?.trim();
  const managementPost = company?.managementPost?.trim();

  return (
    <Card withBorder padding="lg">
      <Stack gap="lg">
        <Group justify="space-between" wrap="nowrap">
          <Group gap="sm" wrap="nowrap">
            <Avatar size={44} radius="xl" color="brand">
              {initials(profile)}
            </Avatar>
            <Text fw={600} fz="lg">
              {profile.name || fullName || profile.email}
            </Text>
          </Group>
          <Badge color={profile.isVerified ? 'lime' : 'gray'} variant="light">
            {profile.isVerified ? t('app.profile.verified') : t('app.profile.notVerified')}
          </Badge>
        </Group>

        <Stack gap="xs">
          <Text c="gray.6" size="xs" tt="uppercase" fw={600}>
            {t('app.profile.fromRegistry')}
          </Text>
          <InfoRow label={t('app.profile.regType')} value={typeLabel} />
          {profile.inn ? <InfoRow label={t('auth.fields.inn')} value={profile.inn} mono /> : null}
          <InfoRow label={t('auth.fields.email')} value={profile.email} />
          {profile.phone ? <InfoRow label={t('auth.fields.phone')} value={profile.phone} /> : null}
        </Stack>

        <Divider />

        {/* Руководитель организации — свойство организации (ЕГРЮЛ/ДаData), только чтение. */}
        <Stack gap="xs">
          <Text c="gray.6" size="xs" tt="uppercase" fw={600}>
            {t('app.profile.managementTitle')}
          </Text>
          <InfoRow label={t('app.company.card.managementName')} value={managementName || '—'} />
          <InfoRow label={t('app.company.card.managementPost')} value={managementPost || '—'} />
          <Text size="xs" c="gray.6">
            {t('app.profile.contactsManagedHint')}
          </Text>
        </Stack>
      </Stack>
    </Card>
  );
}
